import { parseClaimHoldMs } from "@gooner/shared";

/**
 * What a screen has been told about itself: whether it wears a party QR, and how long a
 * claim holds it against the next person.
 *
 * IN THIS BROWSER, not on the device row, and the split is on purpose. `partyQr` never
 * leaves here at all — whether a badge is drawn is a render decision and the server has no
 * use for it. `holdMs` does travel, because a hold has to be enforced where claims land;
 * it rides the announce (and one small POST when the dialog changes it), never the poll.
 *
 * KEYED BY DEVICE. Storage is per-ORIGIN, which is the same trap `DeviceScreen` records
 * about identity: two monitors driven from one computer share a browser profile, and a
 * single flat setting would make them one screen. The screen with no device yet — a
 * `/screen` waiting to be claimed — sits under `""`, the key `usePendingScreen` already
 * files its ambient identity under, and moves onto the device id when it is claimed.
 *
 * ONE CAPPED BLOB rather than a key per device. A device is throwaway — every claim mints
 * or moves one — so a key per device would leave an entry behind on every pairing, forever,
 * on a television re-paired nightly. Newest first, oldest evicted; losing the setting on a
 * screen nobody has touched in eight pairings is not a loss anybody feels.
 */
export interface ScreenSettings {
  partyQr: boolean;
  holdMs: number;
}

/**
 * BOTH OFF. A screen nobody has configured must behave exactly the way it does today —
 * and a QR on a wall hands the television to whoever points a camera at it, which is not
 * something anyone should get without asking for it.
 */
export const DEFAULT_SCREEN_SETTINGS: ScreenSettings = { partyQr: false, holdMs: 0 };

const STORAGE_KEY = "gooner.screenSettings";
/** More screens than one person keeps configured at once. Newest first. */
const MAX = 8;

/** The storage key for a screen: its device id, or `""` while it has no device. */
export const screenKey = (deviceId?: string): string => deviceId ?? "";

interface Row extends ScreenSettings {
  key: string;
}

function readAll(): Row[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
      .map((x) => ({
        key: typeof x.key === "string" ? x.key : "",
        partyQr: x.partyQr === true,
        // Through the shared whitelist, so a hand-edited or stale blob cannot put a hold
        // on a screen that the dialog has no way to express or to clear.
        holdMs: parseClaimHoldMs(x.holdMs),
      }))
      .slice(0, MAX);
  } catch {
    // Unparseable, or storage blocked (private mode, disabled cookies). Defaults are the
    // pre-existing behaviour of every screen, so degrading to them costs nothing.
    return [];
  }
}

function writeAll(rows: Row[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(0, MAX)));
  } catch {
    /* storage full or blocked — these are conveniences, never a source of truth */
  }
}

export function readScreenSettings(key: string): ScreenSettings {
  const row = readAll().find((r) => r.key === key);
  return row
    ? { partyQr: row.partyQr, holdMs: row.holdMs }
    : { ...DEFAULT_SCREEN_SETTINGS };
}

/** Remember a screen's settings, newest first. */
export function writeScreenSettings(key: string, next: ScreenSettings): void {
  const rest = readAll().filter((r) => r.key !== key);
  writeAll([{ key, ...next }, ...rest]);
}

/**
 * Move a pending screen's settings onto the device it just became.
 *
 * The mirror of `rekeyToDevice` in usePendingScreen, and it has to happen at the same
 * moment: somebody standing at a television turned party mode on BEFORE it was claimed,
 * and the claim they were setting it up for must not be the thing that forgets it.
 */
export function rekeyScreenSettings(deviceId: string): void {
  const rows = readAll();
  const held = rows.find((r) => r.key === "");
  if (!held) return;
  writeAll([
    { ...held, key: deviceId },
    ...rows.filter((r) => r.key !== "" && r.key !== deviceId),
  ]);
}
